// DoctrineDisplayLine — shows a city's archetype doctrine with color-coded accent.

import { useT } from '../../i18n/index.js';
import type { Archetype } from '../DoctrinePanel/index.js';

export const ARCHETYPE_CONFIG = {
  industry: { color: '#E67E22', icon: '⚙', label: 'Industry' },
  science: { color: '#3498DB', icon: '🔬', label: 'Science' },
  faith: { color: '#F1C40F', icon: '✝', label: 'Faith' },
  arcane: { color: '#9B59B6', icon: '✦', label: 'Arcane' },
  nature: { color: '#27AE60', icon: '❧', label: 'Nature' },
  chaos: { color: '#E74C3C', icon: '⦿', label: 'Chaos' },
} as const;

interface Props {
  readonly archetype: Archetype | null;
  readonly hasArtifactBonus: boolean;
  readonly borderColor?: string;
}

export function DoctrineDisplayLine({ archetype, hasArtifactBonus, borderColor }: Props) {
  const t = useT();
  if (!archetype) {
    return (
      <div className="flex items-center gap-2 border-l-[4px] border-solid border-l-muted-foreground/50 px-2 py-1">
        <span className="text-[13px] text-muted-foreground">{t('doctrine.unspecialized')}</span>
      </div>
    );
  }

  const config = ARCHETYPE_CONFIG[archetype];
  const effectiveBorderColor = borderColor ?? config.color;

  return (
    <div
      className="flex items-center gap-2 border-l-[4px] border-solid px-2 py-1"
      style={{ borderColor: effectiveBorderColor }}
    >
      <span className="text-[15px]" aria-hidden>
        {config.icon}
      </span>
      <span
        className="text-[13px] font-medium"
        style={{ color: `color-mix(in oklch, ${config.color} 65%, var(--foreground))` }}
      >
        {t(`archetype.${archetype}`)}
      </span>
      {hasArtifactBonus && (
        <span
          className="text-[13px]"
          title={t('doctrine.artifactBonus')}
          aria-label={t('doctrine.artifactBonusLabel')}
        >
          ⚡
        </span>
      )}
    </div>
  );
}
